/**
 * ShiftDetailPage
 * CCH Care — Phase 5f
 *
 * Shows shift header info + resident list with per-resident ADL
 * completion status. Tap a resident to view ADLSummaryPage.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { COLORS } from '../../config/constants';
import client from '../../api/client';

import { formatTime } from '../../utils/formatters';
const BASE = '/shifts/history.php';

const SECTION_COUNT = 8;

function completionColor(rate) {
  if (rate >= 100) return COLORS.accent;
  if (rate >= 50)  return COLORS.warning;
  return COLORS.danger;
}

function completionLabel(signed) {
  if (signed >= SECTION_COUNT) return { label: 'Complete',    color: COLORS.accent,   bg: COLORS.accentLight };
  if (signed > 0)              return { label: 'In Progress', color: COLORS.warning,  bg: COLORS.warningLight };
  return                              { label: 'Not Started', color: COLORS.textMuted, bg: COLORS.bg };
}

export default function ShiftDetailPage() {
  const { shiftId } = useParams();
  const navigate    = useNavigate();
  const [shift, setShift]         = useState(null);
  const [residents, setResidents] = useState([]);
  const [stats, setStats]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.get(`${BASE}?action=detail&care_shift_id=${shiftId}`);
      if (res.data.success) {
        setShift(res.data.shift);
        setResidents(res.data.residents || []);
        setStats(res.data.stats || null);
      } else {
        setError(res.data.error || 'Failed to load');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load shift');
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const shiftDate = shift
    ? new Date(shift.shift_date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      })
    : '';

  return (
    <div style={{ paddingBottom: 80 }}>

      {/* Header */}
      <div style={{
        padding: '12px 16px', background: COLORS.primary, color: '#fff',
        display: 'flex', alignItems: 'center', gap: 12,
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/dashboard/shift-history')}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '2px 6px' }}
        >
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          {shift ? (
            <>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{shift.shift_name} — {shift.unit_name}</div>
              <div style={{ fontSize: 12, opacity: 0.85 }}>{shiftDate}</div>
            </>
          ) : (
            <div style={{ fontSize: 16, fontWeight: 700 }}>Shift Detail</div>
          )}
        </div>
        {stats && (
          <div style={{
            background: 'rgba(255,255,255,0.15)', borderRadius: 8,
            padding: '4px 12px', textAlign: 'center', flexShrink: 0,
          }}>
            <div style={{ fontSize: 18, fontWeight: 800 }}>{stats.completion_rate}%</div>
            <div style={{ fontSize: 9, opacity: 0.8, textTransform: 'uppercase' }}>Complete</div>
          </div>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: COLORS.textMuted }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
          Loading shift…
        </div>
      )}

      {error && !loading && (
        <div style={{ margin: 16, padding: '12px 16px', borderRadius: 10, background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`, color: COLORS.danger, fontSize: 13 }}>
          ⚠️ {error}
        </div>
      )}

      {!loading && !error && shift && (
        <div style={{ padding: 16 }}>

          {/* Stats bar */}
          {stats && stats.total > 0 && (
            <div style={{
              background: COLORS.surface, borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              padding: '12px 16px', marginBottom: 16,
            }}>
              {/* Progress bar */}
              <div style={{ height: 6, background: COLORS.border, borderRadius: 3, marginBottom: 12, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: 3,
                  background: completionColor(stats.completion_rate),
                  width: `${stats.completion_rate}%`,
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', gap: 0 }}>
                {[
                  { label: 'Complete',    value: stats.completed,    color: COLORS.accent   },
                  { label: 'In Progress', value: stats.partial,      color: COLORS.warning  },
                  { label: 'Not Started', value: stats.not_started,  color: COLORS.textMuted },
                  { label: 'Residents',   value: stats.total,        color: COLORS.primary  },
                ].map((s, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', borderRight: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Resident list */}
          {residents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: COLORS.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div>No ADL records for this shift.</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 8, letterSpacing: '0.05em' }}>
                Residents ({residents.length})
              </div>
              {residents.map(r => {
                const badge = completionLabel(r.sections_signed);
                return (
                  <div
                    key={r.adl_record_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/dashboard/shift-history/${shiftId}/adl/${r.adl_record_id}`)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') navigate(`/dashboard/shift-history/${shiftId}/adl/${r.adl_record_id}`); }}
                    style={{
                      background: COLORS.surface, borderRadius: 10,
                      border: `1px solid ${COLORS.border}`,
                      padding: '12px 14px', marginBottom: 8,
                      display: 'flex', alignItems: 'center', gap: 12,
                      cursor: 'pointer',
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                      background: r.sections_signed >= SECTION_COUNT ? COLORS.accentLight : COLORS.primaryLight,
                      color: r.sections_signed >= SECTION_COUNT ? COLORS.accent : COLORS.primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 700,
                    }}>
                      {r.first_name[0]}{r.last_name[0]}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
                        {r.first_name} {r.last_name}
                      </div>
                      <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                        Rm {r.room_number}{r.bed_label ? ` · ${r.bed_label}` : ''}
                        {r.last_signed && (
                          <span style={{ marginLeft: 6 }}>
                            · Last signed {formatTime(r.last_signed)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sections progress */}
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: badge.color, marginBottom: 2 }}>
                        {r.sections_signed}/{SECTION_COUNT}
                      </div>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: badge.color,
                        background: badge.bg, padding: '2px 6px', borderRadius: 4,
                      }}>
                        {badge.label}
                      </span>
                    </div>

                    {/* Supervisor badge */}
                    {r.supervisor_cosigned_at && (
                      <div style={{
                        fontSize: 9, fontWeight: 700, color: COLORS.primary,
                        background: COLORS.primaryLight, padding: '2px 6px', borderRadius: 4,
                        flexShrink: 0,
                      }}>
                        ✓ SUP
                      </div>
                    )}

                    <span style={{ color: COLORS.textMuted, fontSize: 16, flexShrink: 0 }}>›</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
